<script lang="ts">
    import { Block, Link, NavRight, Navbar, Page, Range, Toggle, List, ListItem, NavLeft } from 'framework7-svelte';
    import { _ } from 'svelte-i18n';
    
    import logo from '../assets/lisa-buko.svg';
    import {
        MEASUREMENT_DURATION_FIELDS,
        MEASUREMENT_FIELD_ROUTE_ID,
        MEASUREMENT_FIELD_TIMESTAMP,
        MEASUREMENT_FIELD_ROUTE_NAME,
        ROUTE_MEASUREMENT_TABLE_FIELDS, MEASUREMENT_FIELD_END_GATE_NAME,
        MEASUREMENT_FIELD_START_GATE_NAME,
        SECTION_MEASUREMENT_TABLE_FIELDS
    } from "../constants/measurement-table-constants";
    import type {BaseMeasurements} from "../models/base-measurements.model";
    import {onMount} from "svelte";
    import {formatDuration} from "../utils/style";
    import {sectionMeasurementsDateTimesStore} from "../stores/section-measurement-date-times.store";
    import {routeInfoStore} from "../stores/route-info.store";
    import {MeasurementTableType} from "../types/measurements/measurement-table.type";
    import {getMeasurementFieldLabel} from "../i18n/translate";

    const ROUTES_CHOICE_ALL = 'allRoutes';
    const ROUTES_CHOICE_ACTIVE = 'activeRoutes';
    const ROUTES_CHOICE_CUSTOM = 'customRoutes';
    const PAGE_ROW_COUNT = 100;

    export let tableType: MeasurementTableType;
    export let allMeasurements: BaseMeasurements[];
    
    $: visibleMeasurements = applyFilter(allMeasurements, fromDate, toDate, routesChoice, selectedEntityNames);
    $: sortedMeasurements = sort(visibleMeasurements, sortColumn, sortDirection);
    $: dateFilterDisplayText = (fromDate === toDate && fromDate !== undefined && toDate !== undefined)
        ? fromDate.toLocaleString()
        : `${fromDate.toLocaleString()} - ${toDate.toLocaleString()}`

    let title: string = tableType === MeasurementTableType.Section 
        ? $_('measurements.SECTION_TABLE_TITLE') 
        : $_('measurements.ROUTE_TABLE_TITLE');
    
    let headers: string[] = tableType === MeasurementTableType.Section
        ? SECTION_MEASUREMENT_TABLE_FIELDS
        : ROUTE_MEASUREMENT_TABLE_FIELDS;
    
    let selectableEntityNames: string[] = [];
    let selectedEntityNames: string[] = [];

    let routesChoice: string = ROUTES_CHOICE_ALL;
    let sortColumn: string;
    let sortDirection: number = 1;
    let visibleRowCount = 1000;

    // By default, follow live data is true and we only show the most recent measurement results
    let followLiveData = true;
    let fromDate: Date = $sectionMeasurementsDateTimesStore[$sectionMeasurementsDateTimesStore.length - 1];
    let toDate: Date = $sectionMeasurementsDateTimesStore[$sectionMeasurementsDateTimesStore.length - 1];
    
    sectionMeasurementsDateTimesStore

    const textColumns = [
        MEASUREMENT_FIELD_ROUTE_NAME,
        MEASUREMENT_FIELD_START_GATE_NAME,
        MEASUREMENT_FIELD_END_GATE_NAME,
    ];
    
    onMount(() =>{
        setSelectableEntityNames(); 
    })
    
    function setSelectableEntityNames() {
        let allNames = [];
        
        if(tableType === MeasurementTableType.Section){
            allNames = allMeasurements.map(
                (measurement) => measurement[MEASUREMENT_FIELD_START_GATE_NAME]
            );
        }
        else {
            allNames = allMeasurements.map(
                (measurement) => measurement[MEASUREMENT_FIELD_ROUTE_NAME]
            );
        }
        
        // By converting the list of values to a Set, we remove duplicates.
        selectableEntityNames = [...new Set(allNames)].sort();
    }
    
    function applyFilter(measurements: BaseMeasurements[], fromDate: Date, toDate: Date, routeChoice: string, selection: string[]) {
        let measurementsFilteredByRoute: BaseMeasurements[] = applyRouteFilter(measurements, routeChoice, selection)
        
        return measurementsFilteredByRoute.filter((measurement) => {
            return new Date(measurement.timestamp) >= fromDate && new Date(measurement.timestamp) <= toDate;
        });
    }
    
    function applyRouteFilter(measurements: BaseMeasurements[], routeChoice: string, selection: string[]): BaseMeasurements[] {
        if(routeChoice === ROUTES_CHOICE_ALL){
            return measurements;
        }
        else if(routeChoice === ROUTES_CHOICE_ACTIVE){
            return measurements.filter(
                (measurement) => {
                    const routeId = measurement[MEASUREMENT_FIELD_ROUTE_ID];
                    return !$routeInfoStore[routeId].hidden
                }
            );
        }
        else if(routeChoice === ROUTES_CHOICE_CUSTOM){
            return applyRouteSelectionFilter(measurements, selection);
        }
        
        return [];
    }
    
    function applyRouteSelectionFilter(measurements: BaseMeasurements[], selection: string[]): BaseMeasurements[]  {
        if(selection.length === 0) {
            return [];
        }
        
        if(tableType === MeasurementTableType.Section){
            return measurements.filter(
                (measurement) => selection.includes(measurement[MEASUREMENT_FIELD_START_GATE_NAME]) 
                    || selection.includes(measurement[MEASUREMENT_FIELD_END_GATE_NAME])
            );
        }
        else {
            return measurements.filter(
                (measurement) => selection.includes(measurement[MEASUREMENT_FIELD_ROUTE_NAME])
            );
        }
    }

    function getSortClass(header: string, sortColumn: string, sortDirection: number): string {
        if (sortColumn !== header) 
            return '';
        
        return 'sortable-cell-active ' + (sortDirection === 1 ? 'sortable-asc' : 'sortable-desc');
    }

    function toggleSort(header: string) {
        if (sortColumn === header) {
            sortDirection = -sortDirection;
        } else {
            sortColumn = header;
            sortDirection = 1;
        }
    }

    function sort(data: BaseMeasurements[], sortColumn: string, sortDirection: number): BaseMeasurements[] {
        if(textColumns.includes(sortColumn)) {
            data.sort((a, b) => sortDirection * a[sortColumn].localeCompare(b[sortColumn]));
        } else if(sortColumn === MEASUREMENT_FIELD_TIMESTAMP) {
            data.sort((a, b) => sortDirection * a[sortColumn].getTime() - b[sortColumn].getTime());
        } else {
            data.sort((a, b) => sortDirection * ((a[sortColumn] ?? 0) - (b[sortColumn] ?? 0)));
        }
        return data;
    }

    function generateCsv() {
        let csvHeaders = headers.map(header => getMeasurementFieldLabel(header));
        let csv: string[] = [ csvHeaders.join(',') ];
        
        for (const data of sortedMeasurements) {
            csv.push(
                headers.map(
                    header => formatCsvValueByPropertyName(header, data[header])
                ).join(',')
            );
        }
        return csv.join('\n');
    }

    function exportCsv() {
        const blob = new Blob([generateCsv()], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = title + 's.csv';
        link.click();
        URL.revokeObjectURL(url);
    }
    
    function getElementClassByPropertyName(propertyName: string){
        if(textColumns.includes(propertyName) || propertyName === MEASUREMENT_FIELD_TIMESTAMP)
            return 'label-cell';
        
        return 'numeric-cell';
    }
    
    function formatCellValueByPropertyName(propertyName: string, value: any){
        if(propertyName === MEASUREMENT_FIELD_TIMESTAMP)
            return value.toLocaleString();
        
        if(textColumns.includes(propertyName))
            return value;

        if (value === undefined || isNaN(value))
            return '-';
        
        if(MEASUREMENT_DURATION_FIELDS.includes(propertyName))
            return formatDuration(value);
        
        return value.toFixed(2);
    }
    
    function formatCsvValueByPropertyName(propertyName: string, value: any){
        if(propertyName === MEASUREMENT_FIELD_TIMESTAMP)
            return value.toISOString();

        if(textColumns.includes(propertyName))
            return value;

        if (value === undefined || isNaN(value))
            return '-';
        
        return value.toFixed(2);
    }

    async function onTimeRangeChange(values: number[]) {
        const firstIndex = values[0];
        const lastIndex = values[1];
        
        if(firstIndex !== $sectionMeasurementsDateTimesStore.length - 1 
            || lastIndex !== $sectionMeasurementsDateTimesStore.length - 1){
            followLiveData = false;
        }
        
        fromDate = $sectionMeasurementsDateTimesStore[firstIndex];
        toDate = $sectionMeasurementsDateTimesStore[lastIndex];
    }
</script>

<Page infinite infiniteDistance={50} onInfinite={() => (visibleRowCount += PAGE_ROW_COUNT)} infinitePreloader={false}>
    <Navbar>
        <NavLeft>
            <img class="logo" src={logo} alt="logo" />
            <span>{title}</span>
        </NavLeft>
        <NavRight>
            <Link on:click={exportCsv}>{$_('shared.EXPORT_CSV')}</Link>
            <Link popupClose>{$_('shared.CLOSE')}</Link>
        </NavRight>
    </Navbar>
    <Block>
        <div>
            <label>
                <input type="radio" bind:group={routesChoice} value={ROUTES_CHOICE_ALL} />
                {$_('pages.project.ALL_ROUTES')}
            </label>
            <label>
                <input type="radio" bind:group={routesChoice} value={ROUTES_CHOICE_ACTIVE} />
                {$_('pages.project.ACTIVE_ROUTES')}
            </label>
            <label>
                <input type="radio" bind:group={routesChoice} value={ROUTES_CHOICE_CUSTOM} />
                {$_('pages.project.CUSTOM')}
            </label>
            {#if routesChoice === ROUTES_CHOICE_CUSTOM}
                <List>
                    <ListItem
                        title="Filter {title.toLowerCase()}s" smartSelect
                        smartSelectParams={{ openIn: 'popup', searchbar: true, searchbarPlaceholder: $_('shared.SEARCH') + title.toLowerCase() }}
                    >
                        <select multiple bind:value={selectedEntityNames}>
                            {#each selectableEntityNames as option}
                                <option value={option}>{option}</option>
                            {/each}
                        </select>
                    </ListItem>
                </List>
            {/if}
        </div>
        <div style="width: 95%">
            <!-- Obscure #key is necessary to make "max" property responsive. See https://svelte.dev/repl/cdec4ea57d9c420e8d9fd87f6f59d43d?version=3.38.2 -->
            {#key [$sectionMeasurementsDateTimesStore]}
                <Range 
                    min={0} max={$sectionMeasurementsDateTimesStore.length - 1} 
                    step={1} dual={true} label={true} 
                    value={$sectionMeasurementsDateTimesStore.map(date => date.getTime())} 
                    onRangeChange={onTimeRangeChange} 
                />
            {/key}
            <p>
                {dateFilterDisplayText}
            </p>
        </div>
        <div>
            <Toggle bind:checked={followLiveData}></Toggle>
            {$_("pages.project.FOLLOW_LIVE_DATA")}
        </div>
    </Block>
    <div class="data-table">
        <table>
            <thead>
                <tr>
                    {#each headers as header}
                        <th 
                            class="{getElementClassByPropertyName(header)} sortable-cell 
                                {getSortClass(header, sortColumn, sortDirection)}" 
                            on:click={() => toggleSort(header)}
                        >
                            {getMeasurementFieldLabel(header)}
                        </th>
                    {/each}
                </tr>
            </thead>
            <tbody>
                {#each sortedMeasurements as measurement}
                    <tr>
                        {#each headers as header}
                            <td class="{getElementClassByPropertyName(header)}">
                                {formatCellValueByPropertyName(header, measurement[header])}
                            </td>
                        {/each}
                    </tr>
                {/each}
            </tbody>
        </table>
    </div>
</Page>
